import * as React from 'react'

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

type UseAnimatedProgressOptions = {
  durationMs?: number
  round?: boolean
}

/**
 * Animates displayedProgress from previous value to target value over durationMs.
 * Input/output are 0..1.
 */
export const useAnimatedProgress = (
  targetProgress: number,
  { durationMs = 250, round = true }: UseAnimatedProgressOptions = {}
) => {
  const target = clamp01(targetProgress)

  const [displayed, setDisplayed] = React.useState(target)
  const rafRef = React.useRef<number | null>(null)
  const fromRef = React.useRef(target)
  const startRef = React.useRef<number>(0)

  React.useEffect(() => {
    const from = fromRef.current
    const to = target
    if (from === to) return

    if (rafRef.current) cancelAnimationFrame(rafRef.current)

    startRef.current = performance.now()
    const delta = to - from

    const tick = (now: number) => {
      const elapsed = now - startRef.current
      const t = Math.min(elapsed / durationMs, 1)

      const value = from + delta * t
      const v = clamp01(value)

      setDisplayed(round ? Math.round(v * 100) / 100 : v)

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = to
        rafRef.current = null
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [target, durationMs, round])

  return displayed
}
